import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, ConfirmModal, useToast } from "@/components/ui";
import { eventsApi } from "@/lib/api/endpoints";
import type { EventSummary } from "@/lib/types/api";
import { IconDownload, IconExternal, IconLink, IconSliders, IconTrash, IconUpload } from "./icons";

/**
 * One event, plus the public booth link it publishes.
 *
 * The link is the whole point of the console: an operator configures an event
 * here and hands `/<slug>` to a kiosk. It's surfaced as a one-tap copy rather
 * than something to be reconstructed by hand.
 */
export function EventCard({ event, onChanged }: { event: EventSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [busy, setBusy] = useState(false);

  const boothUrl = `${window.location.origin}/${event.slug}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(boothUrl);
      toast.success("Booth link copied");
    } catch {
      toast.error("Could not copy — your browser blocked clipboard access");
    }
  }

  async function archive() {
    setBusy(true);
    try {
      await eventsApi.archive(event.id);
      toast.success(`${event.name} archived`);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not archive that event");
    } finally {
      setBusy(false);
      setConfirmArchive(false);
    }
  }

  async function importConfig(file: File | undefined) {
    if (!file) return;
    try {
      await eventsApi.importConfig(event.id, await file.text());
      toast.success("Configuration imported");
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Import failed");
    }
  }

  return (
    <article className="panel group flex flex-col gap-4 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-lift">
      <header className="flex items-start justify-between gap-3">
        <StatusBadge status={event.status} hasDraft={event.draft_config_present} />
        <span className="label-eyebrow">{event.event_type}</span>
      </header>

      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold leading-tight text-haze-100">{event.name}</h3>
        <button
          onClick={copyLink}
          title="Copy booth link"
          className="group flex w-fit items-center gap-1.5 font-mono text-xs text-haze-400 transition-colors hover:text-accent"
        >
          /{event.slug}
          <IconLink size={11} className="opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      </div>

      <dl className="flex gap-4 border-t border-ink-600 pt-3 text-xs text-haze-400">
        <div className="flex gap-1"><dt>{event.scenes_count}</dt><dd>Vibes</dd></div>
        <div className="flex gap-1"><dt>{event.participants_count}</dt><dd>Acts</dd></div>
        {typeof event.moments_count === "number" && (
          <div className="flex gap-1"><dt>{event.moments_count}</dt><dd>Moments</dd></div>
        )}
      </dl>

      <footer className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => navigate(`/admin/events/${event.id}/builder`)}
        >
          Edit Config
        </Button>

        <IconAction label="Open booth" onClick={() => window.open(boothUrl, "_blank", "noopener")}>
          <IconExternal size={14} />
        </IconAction>

        <label className="cursor-pointer">
          <IconAction label="Import YAML" as="span"><IconUpload size={14} /></IconAction>
          <input
            type="file"
            accept=".yml,.yaml"
            hidden
            onChange={(e) => void importConfig(e.target.files?.[0])}
          />
        </label>

        <IconAction label="Export YAML" onClick={() => void eventsApi.exportConfig(event.id, event.slug)}>
          <IconDownload size={14} />
        </IconAction>

        <IconAction label="Site Builder" onClick={() => navigate(`/admin/events/${event.id}/builder`)}>
          <IconSliders size={14} />
        </IconAction>

        <IconAction label="Archive" destructive onClick={() => setConfirmArchive(true)}>
          <IconTrash size={14} />
        </IconAction>
      </footer>

      <ConfirmModal
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={archive}
        loading={busy}
        destructive
        title={`Archive ${event.name}?`}
        description="The booth link stops serving and the event leaves your active list. Generations and configuration are kept, and it can be restored from the Site Builder."
        confirmLabel="Archive event"
      />
    </article>
  );
}

function IconAction({
  children, label, onClick, destructive, as = "button",
}: {
  children: ReactNode;
  label: string;
  onClick?: () => void;
  destructive?: boolean;
  as?: "button" | "span";
}) {
  const Tag = as;
  return (
    <Tag
      {...(as === "button" ? { type: "button" as const, onClick } : {})}
      title={label}
      aria-label={label}
      className={[
        "grid h-10 w-10 place-items-center rounded-xl border border-ink-500 bg-ink-800 transition-colors",
        destructive ? "text-haze-400 hover:border-danger/60 hover:text-danger" : "text-haze-300 hover:border-accent/60 hover:text-accent",
      ].join(" ")}
    >
      {children}
    </Tag>
  );
}

function StatusBadge({ status, hasDraft }: { status: string; hasDraft: boolean }) {
  if (status === "published") {
    return (
      <span className="flex items-center gap-1.5">
        <Badge tone="aqua">Published</Badge>
        {hasDraft && <Badge tone="warn">Draft</Badge>}
      </span>
    );
  }
  if (status === "archived") return <Badge tone="neutral">Archived</Badge>;
  return <Badge tone="warn">Draft</Badge>;
}
